// src/Pages/Projects.js
import React from "react";
import { FaGithub, FaExternalLinkAlt } from "react-icons/fa";
import { BsFiletypeJson } from "react-icons/bs";
import {
  SiReact,
  SiRedis,
  SiMongodb,
  SiNodedotjs,
  SiExpress,
  SiHeroku,
  SiFirebase,
  SiGraphql,
  SiHandlebarsdotjs,
  SiHtml5,
  SiCss3,
  SiRedux,
} from "react-icons/si";

const projectsData = [
  {
    title: "NJ Rental",
    bullets: [
      "Collaborated in a cross-functional team of 4 members to develop and deploy an apartment rental e-commerce website, enabling users to list, rent, and communicate with property owners.",
      "Leveraged React.js for the front-end, Redis for caching, and MongoDB for the database, while utilizing Node.js and Express.js for server-side logic and WebSocket for chat features.",
      "Employed Heroku for efficient deployment, ensuring a reliable and scalable platform.",
    ],
    websiteLink: "https://nj-rental.herokuapp.com/",
    githubLink:
      "https://github.com/CsProjectGroup554-Inferno/CS554_FinalProject",
    techStack: [SiReact, SiRedis, SiMongodb, SiNodedotjs, SiExpress, SiHeroku, BsFiletypeJson],
  },
  {
    title: "Zero Waste",
    bullets: [
      "Worked in a 4-member team to create a restaurant inventory website where users can create recipes and add items to their inventory.",
      "Enabled Google-account login for seamless authentication.",
      "Used React.js for the front-end, MongoDB & Mongoose for data modeling, and Node.js with Express.js for the backend; Firebase powered user authentication.",
    ],
    githubLink: "https://github.com/BriannaPGarland/ZeroWaste",
    techStack: [SiReact, SiMongodb, SiNodedotjs, SiExpress, SiFirebase, BsFiletypeJson],
  },
  {
    title: "Bintrest",
    bullets: [
      "Built a Pinterest-style website using the Unsplash API to load posts; cached ‘binned’ posts for offline access.",
      "Utilized React.js on the front-end, Node.js on the backend, and Redis for caching.",
      "Integrated Apollo Client for GraphQL queries to manage and fetch data efficiently.",
    ],
    githubLink: "https://github.com/yourusername/Bintrest",
    techStack: [SiReact, SiNodedotjs, SiRedis, SiGraphql, BsFiletypeJson],
  },
  {
    title: "Calmify",
    bullets: [
      "Acted as Scrum Master, leading the development of a mental wellness app using agile methodologies.",
      "Enabled users to engage in music therapy and play mini-games for relaxation and emotional well-being.",
      "Collaborated closely with stakeholders to gather requirements and debug issues, ensuring alignment with customer needs.",
    ],
    githubLink:
      "https://github.com/ssw555tm092022Spring/ssw555tm092022Spring",
    techStack: [SiReact, SiRedux, BsFiletypeJson],
  },
  {
    title: "Playmore",
    bullets: [
      "Developed a site using Node.js, Handlebars, MongoDB, HTML, CSS, and Express.js to showcase local playgrounds.",
      "Implemented user registration, commenting, liking, and match-organizing features.",
      "Built an integrated booking system allowing users to reserve playgrounds for various activities.",
    ],
    githubLink: "https://github.com/siddhantkumar052/Play-More",
    techStack: [
      SiNodedotjs,
      SiExpress,
      SiMongodb,
      SiHandlebarsdotjs,
      SiHtml5,
      SiCss3,
      BsFiletypeJson,
    ],
  },
];

export default function Projects() {
  return (
    <section className="projects-surface bg-[#f1f5f9] py-8 transition-colors duration-300 dark:bg-slate-950">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8">
        <h2 className="mb-6 text-3xl font-bold text-[#213448] dark:text-slate-100">
          Projects
        </h2>

        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {projectsData.map((proj, idx) => (
            <div
              key={idx}
              className="project-card bg-slate-50 border border-gray-200 rounded-lg \
                         p-3 sm:p-4 max-w-xs w-full mx-auto \
                         hover:shadow-lg transition flex flex-col justify-between h-full dark:border-slate-700 dark:bg-slate-900"
            >
              <h3 className="mb-2 text-lg font-semibold text-[#213448] dark:text-slate-100">
                {proj.title}
              </h3>

              <ul className="mb-4 list-inside list-disc space-y-1 text-sm text-gray-600 dark:text-slate-300">
                {proj.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>

              {proj.techStack && (
                <div className="flex flex-wrap items-center space-x-2 mb-4">
                  {proj.techStack.map((Icon, i) => {
                    const raw = Icon.displayName || Icon.name || "";
                    const label = raw.replace(/^Si/, "");
                    return (
                      <Icon
                        key={i}
                        size={18}
                        title={label}
                        className="text-gray-500 transition hover:text-[#213448] dark:text-slate-400 dark:hover:text-slate-100"
                      />
                    );
                  })}
                </div>
              )}

              <div className="mt-auto pt-2 flex flex-wrap gap-2">
                <a
                  href={proj.githubLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    inline-flex items-center space-x-1
                    px-2 py-1 text-xs font-semibold
                    rounded border border-gray-300 text-[#213448]
                    hover:bg-orange-500 hover:text-[#F1F0E8]
                    hover:border-transparent transition dark:border-slate-600 dark:text-slate-100 dark:hover:border-[#F59E0B] dark:hover:bg-[#F59E0B]
                  "
                >
                  <FaGithub size={14} />
                  <span>Code</span>
                </a>

                {proj.websiteLink && (
                  <a
                    href={proj.websiteLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
                      inline-flex items-center space-x-1
                      px-2 py-1 text-xs font-medium
                      rounded border border-gray-300 text-[#213448]
                      hover:bg-orange-500 hover:text-[#F1F0E8]
                      hover:border-transparent transition dark:border-slate-600 dark:text-slate-100 dark:hover:border-[#F59E0B] dark:hover:bg-[#F59E0B]
                    "
                  >
                    <FaExternalLinkAlt size={12} />
                    <span>Live</span>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
